import type { ValidationResultUI } from '../types';
import clsx from 'clsx';

interface ValidationResultsProps {
  validation?: ValidationResultUI;
}

export default function ValidationResults({ validation }: ValidationResultsProps) {
  if (!validation) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <p className="text-gray-500">No validation results available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Validation Summary */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Validation Summary</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-500">Status:</span>
            <span className={clsx(
              'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
              validation.isValid
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            )}>
              {validation.isValid ? 'Valid' : 'Invalid'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-500">Confidence:</span>
            <span className="text-sm text-gray-900">
              {(validation.overallConfidence * 100).toFixed(0)}%
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-500">Review Required:</span>
            <span className={clsx(
              'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
              validation.requiresReview
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-gray-100 text-gray-800'
            )}>
              {validation.requiresReview ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
      </div>

      {/* Errors */}
      {validation.errors.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Errors ({validation.errors.length})
          </h3>
          <ul className="space-y-3">
            {validation.errors.map((error, index) => (
              <li key={index} className="flex items-start">
                <svg className="flex-shrink-0 h-5 w-5 text-red-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="ml-3">
                  <p className="text-sm text-gray-900">
                    <span className="font-medium">{error.field}:</span> {error.message}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Code: {error.code}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Warnings */}
      {validation.warnings.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Warnings ({validation.warnings.length})
          </h3>
          <ul className="space-y-3">
            {validation.warnings.map((warning, index) => (
              <li key={index} className="flex items-start">
                <svg className="flex-shrink-0 h-5 w-5 text-yellow-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div className="ml-3">
                  <p className="text-sm text-gray-900">
                    <span className="font-medium">{warning.field}:</span> {warning.message}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Code: {warning.code}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Enrichments */}
      {validation.enrichments && Object.keys(validation.enrichments).length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Data Enrichments</h3>
          <div className="space-y-4">
            {Object.entries(validation.enrichments).map(([key, value]) => (
              <div key={key} className="border-b border-gray-200 pb-4 last:border-0">
                <h4 className="text-sm font-medium text-gray-700 capitalize mb-2">{key}</h4>
                {key === 'vat' && typeof value === 'object' && value !== null && (
                  <dl className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Valid:</dt>
                      <dd className={clsx(
                        'font-medium',
                        (value as any).valid ? 'text-green-600' : 'text-red-600'
                      )}>
                        {(value as any).valid ? 'Yes' : 'No'}
                      </dd>
                    </div>
                    {(value as any).name && (
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Company:</dt>
                        <dd className="text-gray-900">{(value as any).name}</dd>
                      </div>
                    )}
                    {(value as any).address && (
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Address:</dt>
                        <dd className="text-gray-900">{(value as any).address}</dd>
                      </div>
                    )}
                  </dl>
                )}
                {key === 'currency' && typeof value === 'object' && value !== null && (
                  <dl className="space-y-1 text-sm">
                    {(value as any).exchangeRate && (
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Exchange Rate:</dt>
                        <dd className="text-gray-900">{(value as any).exchangeRate}</dd>
                      </div>
                    )}
                    {(value as any).convertedAmount && (
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Amount in EUR:</dt>
                        <dd className="text-gray-900">€{(value as any).convertedAmount}</dd>
                      </div>
                    )}
                  </dl>
                )}
                {typeof value !== 'object' && (
                  <p className="text-sm text-gray-900">{String(value)}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}